import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output} from '@angular/core';
import {Unit} from '../../dashboard/monitoring/unit';
import {formatDate} from '@angular/common';
import * as moment from 'moment';
import {Moment} from 'moment';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable, timer} from 'rxjs';
import {distinctUntilKeyChanged, filter, map, switchMap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {LbsYandexModel} from '../../dashboard/monitoring/lbs-yandex.model';
import {NotificationsService} from 'angular2-notifications';
import {ClipboardService} from 'ngx-clipboard';
import {roundToHundredths} from '../../utility/utility-functions';
import {timestampToDurationApproximately} from '../../utility/timestamp-functions';

@Component({
  selector: 'app-unit-info',
  templateUrl: './unit-info.component.html',
  styleUrls: ['./unit-info.component.scss']
})
export class UnitInfoComponent implements OnInit {
  protected readonly roundToHundredths = roundToHundredths;
  unitBehaviorSubject: BehaviorSubject<Unit> = new BehaviorSubject<Unit>(null);
  unitAddress: Observable<any>;
  qrCodeText: Observable<string>;
  qrCodeLabel: Observable<string>;
  qrCodeLink: Observable<string>;
  @Output() searchByLbsClickEmitter: EventEmitter<Unit> = new EventEmitter<Unit>();
  @Input() lbsYandex: BehaviorSubject<LbsYandexModel> = new BehaviorSubject<LbsYandexModel>(null);

  @Input() set unit(value: Unit) {
    this.unitBehaviorSubject.next(value);
  }

  constructor(
    @Inject(LOCALE_ID) protected locale,
    protected translates: TranslateService,
    protected http: HttpClient,
    protected notif: NotificationsService,
    protected clipboard: ClipboardService,
  ) {
    this.qrCodeText = this.unitBehaviorSubject.pipe(
      map(
        unit => {
          if (!unit) {
            return null;
          }
          if (unit.prepaidSharingWebAppUrl) {
            return unit.prepaidSharingWebAppUrl;
          }
          if (!unit.registration_number) {
            return null;
          }
          if (unit.unitModel.project.download_app_link) {
            return unit.unitModel.project.download_app_link + '?id=' + unit.registration_number;
          }
          return unit.registration_number;
        }
      )
    );
    this.qrCodeLabel = this.unitBehaviorSubject.pipe(
      map(
        unit => {
          if (!unit) {
            return null;
          }
          if (!unit.registration_number) {
            return null;
          }
          return unit.registration_number;
        }
      )
    );

    this.qrCodeLink = this.unitBehaviorSubject.pipe(
      map(
        unit => {
          if (!unit) {
            return null;
          }
          if (unit.prepaidSharingWebAppUrl) {
            return unit.prepaidSharingWebAppUrl;
          }
          if (!unit.registration_number) {
            return null;
          }
          if (unit.unitModel.project.download_app_link) {
            return unit.unitModel.project.download_app_link + '?id=' + unit.registration_number;
          }
          return null;
        }
      )
    );
  }

  get unitLastValidMsgTime(): number {
    return this.unitData.lastMsgUnit ? this.unitData.lastMsgUnit.last_valid_coordinates_time : null;
  }

  get unitData() {
    return this.unitBehaviorSubject.getValue();
  }

  get currentTime(): Moment {
    return moment();
  }

  get unitLastMsgTime(): number {
    return this.unitData.lastMsgUnit ? this.unitData.lastMsgUnit.time : null;
  }

  get unitSats(): number {
    return this.unitData.lastMsgUnit ? this.unitData.lastMsgUnit.sats : 0;
  }

  currentTimeObservable: Observable<number> = timer(1000, 1000).pipe(
    map(
      () => Math.floor(Date.now() / 1000)
    )
  );

  lastMsgTimeDuration: Observable<string> = this.currentTimeObservable.pipe(
    map(
      timestamp => {
        if (!this.unitLastMsgTime) {
          return '';
        }
        return '(' + timestampToDurationApproximately(timestamp - this.unitLastMsgTime, this.translates)
          + ' ' + this.translates.instant('ago') + ')';
      }
    )
  );

  lastValidCoordinatesTime: Observable<string> = this.currentTimeObservable.pipe(
    map(
      timestamp => {
        if (!this.unitLastValidMsgTime) {
          return '';
        }
        return '(' + timestampToDurationApproximately(timestamp - this.unitLastValidMsgTime, this.translates)
          + ' ' + this.translates.instant('ago') + ')';
      }
    )
  );

  formatLastMsgTime(timestamp: number): string {
    return formatDate(timestamp * 1000, 'medium', this.locale);
  }

  onCopyClick(lat: number, lon: number) {
    this.clipboard.copyFromContent(`${lat}, ${lon}`);
    this.notif.success(this.translates.instant('Coordinated were copied to clipboard'));
  }

  searchByLbsClick(unit: Unit) {
    this.searchByLbsClickEmitter.emit(unit);
  }

  signalLevelIcon() {
    let iconName = 'signal-level_alarm';
    if ((this.currentTime.unix() - this.unitLastMsgTime) > 300 && (this.currentTime.unix() - this.unitLastMsgTime) < 900) {
      iconName = 'signal-level_normal';
    } else if ((this.currentTime.unix() - this.unitLastMsgTime) > 900 && (this.currentTime.unix() - this.unitLastMsgTime) < 1800) {
      iconName = 'signal-level_warning';
    } else if ((this.currentTime.unix() - this.unitLastMsgTime) < 300) {
      iconName = 'signal-level_success';
    }
    return iconName;
  }

  unitSatelliteInfo(): { iconName: string, title: string } {
    const data: { iconName: string; title: string } = {iconName: '', title: ''};
    if (this.unitSats <= Unit.SATELLITE_ALARM) {
      data.iconName = 'satellite_alarm';
      data.title = this.translates.instant('No signal');
    } else if (this.unitSats >= Unit.SATELLITE_ALARM && this.unitSats <= Unit.SATELLITE_WARNING) {
      data.iconName = 'satellite_warning';
      data.title = this.translates.instant('Weak signal');
    } else if (this.unitSats >= Unit.SATELLITE_WARNING && this.unitSats <= Unit.SATELLITE_NORMAL) {
      data.iconName = 'satellite_normal';
      data.title = this.translates.instant('Normal signal');
    } else if (this.unitSats > Unit.SATELLITE_NORMAL) {
      data.iconName = 'satellite_success';
      data.title = this.translates.instant('Good signal');
    }
    return data;
  }

  ngOnInit() {
    this.unitAddress = this.unitBehaviorSubject.pipe(
      filter(unit => !!unit && !!unit.lastMsgUnit),
      distinctUntilKeyChanged('id'),
      switchMap(
        (unit: Unit) =>
          this.http.get(
            environment.apiBasepoint + 'geocode',
            {
              observe: 'body', params: (new HttpParams()).append('lat', unit.lastMsgUnit.lat.toString())
                .append('lon', unit.lastMsgUnit.lon.toString()).append('type', 'reverse')
            }
          )
      )
    );
  }
}
